import { useEffect, useRef, useState } from 'react';
import { Box, TextField, MenuItem, Autocomplete, Button, InputAdornment } from '@mui/material';
import { Search, RotateCcw } from 'lucide-react';
import type { AuditFilters, AuditMethod } from 'services/auditApi';
import { unixToLocalInput, localInputToUnix } from './helpers';

export interface UserOption {
  id: string;
  email: string;
  name: string;
}

interface FiltersBarProps {
  filters: AuditFilters;
  categories: string[];
  actions: string[];
  users: UserOption[];
  onChange: (patch: Partial<AuditFilters>) => void;
  onCategoryChange: (category: string | undefined) => void;
  onReset: () => void;
}

const HTTP_METHODS: AuditMethod[] = ['POST', 'PATCH', 'DELETE'];

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    fontSize: 13,
    bgcolor: '#fff',
    '& fieldset': { borderColor: 'var(--color-border)' },
    '&:hover fieldset': { borderColor: 'var(--color-border-hover)' },
    '&.Mui-focused fieldset': { borderColor: 'var(--color-primary)' },
  },
  '& .MuiInputLabel-root': { fontSize: 13 },
  '& .MuiInputLabel-root.Mui-focused': { color: 'var(--color-primary)' },
};

export default function FiltersBar({
  filters,
  categories,
  actions,
  users,
  onChange,
  onCategoryChange,
  onReset,
}: FiltersBarProps) {
  // Debounce свободного поиска (300ms), чтобы не дёргать API на каждый символ.
  const [searchText, setSearchText] = useState(filters.search ?? '');
  const lastSent = useRef(filters.search ?? '');

  // Синхронизация при внешнем сбросе фильтров.
  useEffect(() => {
    setSearchText(filters.search ?? '');
    lastSent.current = filters.search ?? '';
  }, [filters.search]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (searchText !== lastSent.current) {
        lastSent.current = searchText;
        onChange({ search: searchText || undefined });
      }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  const selectedUser = users.find((u) => u.id === filters.user_id) ?? null;

  return (
    <Box
      sx={{
        background: '#FFFFFF',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: '0 2px 16px rgba(20,116,255,0.06)',
        p: 2,
        mb: 2,
      }}
    >
      {/* Поиск + сброс */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
        <TextField
          placeholder="Поиск по журналу…"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          size="small"
          fullWidth
          sx={fieldSx}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={16} color="var(--color-text-muted)" />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="outlined"
          startIcon={<RotateCcw size={14} />}
          onClick={onReset}
          sx={{
            flexShrink: 0,
            fontSize: 13,
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: 'var(--radius-md)',
            borderColor: 'var(--color-border)',
            color: 'var(--color-text-secondary)',
            '&:hover': { borderColor: 'var(--color-border-hover)', bgcolor: 'var(--color-bg)' },
          }}
        >
          Сбросить
        </Button>
      </Box>

      {/* Сетка фильтров */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: 1.5,
        }}
      >
        {/* Категория */}
        <TextField
          select
          label="Категория"
          value={filters.category ?? ''}
          onChange={(e) => onCategoryChange(e.target.value || undefined)}
          size="small"
          sx={fieldSx}
        >
          <MenuItem value="">Все</MenuItem>
          {categories.map((c) => (
            <MenuItem key={c} value={c} sx={{ fontSize: 13 }}>
              {c}
            </MenuItem>
          ))}
        </TextField>

        {/* Действие */}
        <TextField
          select
          label="Действие"
          value={filters.action ?? ''}
          onChange={(e) => onChange({ action: e.target.value || undefined })}
          size="small"
          sx={fieldSx}
        >
          <MenuItem value="">Все</MenuItem>
          {actions.map((a) => (
            <MenuItem key={a} value={a} sx={{ fontSize: 13 }}>
              {a}
            </MenuItem>
          ))}
        </TextField>

        {/* HTTP-метод */}
        <TextField
          select
          label="HTTP-метод"
          value={filters.http_method ?? ''}
          onChange={(e) =>
            onChange({ http_method: (e.target.value || undefined) as AuditMethod | undefined })
          }
          size="small"
          sx={fieldSx}
        >
          <MenuItem value="">Все</MenuItem>
          {HTTP_METHODS.map((m) => (
            <MenuItem key={m} value={m} sx={{ fontSize: 13 }}>
              {m}
            </MenuItem>
          ))}
        </TextField>

        {/* Пользователь */}
        <Autocomplete
          options={users}
          value={selectedUser}
          onChange={(_, v) => onChange({ user_id: v?.id || undefined })}
          getOptionLabel={(o) => o.email || o.name || o.id}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          renderInput={(params) => (
            <TextField {...params} label="Пользователь" size="small" sx={fieldSx} />
          )}
        />

        {/* Тип объекта */}
        <TextField
          label="Тип объекта"
          value={filters.subject_type ?? ''}
          onChange={(e) => onChange({ subject_type: e.target.value || undefined })}
          size="small"
          sx={fieldSx}
        />

        {/* ID объекта */}
        <TextField
          label="ID объекта"
          value={filters.subject_id ?? ''}
          onChange={(e) => onChange({ subject_id: e.target.value || undefined })}
          size="small"
          sx={fieldSx}
        />

        {/* Дата с */}
        <TextField
          type="datetime-local"
          label="С"
          value={unixToLocalInput(filters.date_from)}
          onChange={(e) => onChange({ date_from: localInputToUnix(e.target.value) })}
          size="small"
          InputLabelProps={{ shrink: true }}
          sx={fieldSx}
        />

        {/* Дата по */}
        <TextField
          type="datetime-local"
          label="По"
          value={unixToLocalInput(filters.date_to)}
          onChange={(e) => onChange({ date_to: localInputToUnix(e.target.value) })}
          size="small"
          InputLabelProps={{ shrink: true }}
          sx={fieldSx}
        />
      </Box>
    </Box>
  );
}
